import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns'; // (1) مكتبة مفيدة لتنسيق التواريخ
import { 
  getTraineeById, 
  updateTrainee, 
  getAllLawyers, 
  NewTraineeData 
} from '../../../services/traineeService';
import { Lawyer } from '../../../types/lawyer';
import styles from './TraineeForm.module.css'; // إعادة استخدام نفس ملف التنسيق

const EditTraineePage: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // (2) الحصول على ID من الرابط
  const navigate = useNavigate();
  const traineeId = Number(id);

  const [lawyers, setLawyers] = useState<Lawyer[]>([]);
  const { 
    register, 
    handleSubmit, 
    reset, // (3) دالة لإعادة تعيين قيم النموذج
    formState: { errors, isSubmitting } 
  } = useForm<NewTraineeData>();

  // (4) جلب بيانات المتدرب والمحامين عند تحميل الصفحة
  useEffect(() => {
    if (!traineeId) return;

    const fetchData = async () => {
      try {
        // جلب بيانات المتدرب والمحامين بالتوازي
        const [traineeData, lawyersData] = await Promise.all([
          getTraineeById(traineeId),
          getAllLawyers()
        ]);
        
        setLawyers(lawyersData);

        // (5) ملء النموذج بالبيانات الحالية
        if (traineeData) {
          reset({
            ...traineeData,
            // تأكد من تنسيق التواريخ بشكل صحيح لتتوافق مع حقل <input type="date">
            start_date: format(new Date(traineeData.start_date), 'yyyy-MM-dd'),
            end_date: format(new Date(traineeData.end_date), 'yyyy-MM-dd'),
          });
        }
      } catch (error) {
        toast.error('فشل في جلب بيانات المتدرب.');
        navigate('/admin/trainees/list'); // العودة للقائمة في حالة الخطأ
      }
    };

    fetchData();
  }, [traineeId, navigate, reset]);

  // (6) دالة إرسال التحديث
  const onSubmit: SubmitHandler<NewTraineeData> = async (data) => {
    const submissionData = {
      ...data,
      user_id: Number(data.user_id),
      lawyer_id: Number(data.lawyer_id),
    };

    const promise = updateTrainee(traineeId, submissionData);

    toast.promise(promise, {
      loading: 'جاري تحديث البيانات...',
      success: () => {
        navigate('/admin/trainees/list');
        return 'تم تحديث بيانات المتدرب بنجاح!';
      },
      error: (err) => err.response?.data?.message || 'فشل في تحديث البيانات.',
    });
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.formContainer}>
        <h2 className={styles.title}>تعديل بيانات المتدرب</h2>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          
          {/* الحقول مطابقة تمامًا لصفحة الإضافة */}
          <div className={styles.formField}>
            <label htmlFor="name" className={styles.label}>اسم المتدرب</label>
            <input id="name" type="text" {...register('name', { required: 'الاسم مطلوب' })} className={styles.input} />
            {errors.name && <p className={styles.error}>{errors.name.message}</p>}
          </div>

          <div className={styles.formField}>
            <label htmlFor="trainee_number" className={styles.label}>رقم المتدرب</label>
            <input id="trainee_number" type="text" {...register('trainee_number', { required: 'رقم المتدرب مطلوب' })} className={styles.input} />
            {errors.trainee_number && <p className={styles.error}>{errors.trainee_number.message}</p>}
          </div>

          <div className={styles.formField}>
            <label htmlFor="user_id" className={styles.label}>معرف المستخدم (User ID)</label>
            <input id="user_id" type="number" {...register('user_id', { required: 'معرف المستخدم مطلوب', valueAsNumber: true })} className={styles.input} readOnly/>
            {errors.user_id && <p className={styles.error}>{errors.user_id.message}</p>}
          </div>

          <div className={styles.formField}>
            <label htmlFor="lawyer_id" className={styles.label}>المحامي المشرف</label>
            <select id="lawyer_id" {...register('lawyer_id', { required: 'يجب اختيار محامٍ', valueAsNumber: true })} className={styles.input}>
              {lawyers.map(lawyer => (
                <option key={lawyer.id} value={lawyer.id}>
                  {lawyer.name}
                </option>
              ))}
            </select>
            {errors.lawyer_id && <p className={styles.error}>{errors.lawyer_id.message}</p>}
          </div>

          <div className={styles.formField}>
            <label htmlFor="start_date" className={styles.label}>تاريخ البدء</label>
            <input id="start_date" type="date" {...register('start_date', { required: 'تاريخ البدء مطلوب' })} className={styles.input} />
            {errors.start_date && <p className={styles.error}>{errors.start_date.message}</p>}
          </div>

          <div className={styles.formField}>
            <label htmlFor="end_date" className={styles.label}>تاريخ الانتهاء</label>
            <input id="end_date" type="date" {...register('end_date', { required: 'تاريخ الانتهاء مطلوب' })} className={styles.input} />
            {errors.end_date && <p className={styles.error}>{errors.end_date.message}</p>}
          </div>

          <div className={styles.buttonContainer}>
            <Link to="/admin/trainees/list" className={styles.cancelButton}>إلغاء</Link>
            <button type="submit" disabled={isSubmitting} className={styles.submitButton}>
              {isSubmitting ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTraineePage;
